import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { AuiIcon, AuiIconComponent } from '@ral/ui:aui/icons';
import { AuiColor } from '@ral/ui:aui/utils';
import { AuiButtonStyleType } from '@ral/utils:core/ui';
import { hostBinding } from 'ngxtension/host-binding';
@Component({
    selector: 'aui-badge',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, AuiIconComponent, AuiIconComponent],
    template: `
        <div class="flex h-5 flex-nowrap items-center gap-2 empty:hidden">
            @if (icon(); as icon) {
                <div class="flex-none">
                    <aui-icon [icon]="icon"></aui-icon>
                </div>
            }
            @if (label(); as label) {
                <div class="min-w-0 flex-shrink">
                    <span>
                        {{ label }}
                    </span>
                </div>
            }
            @if (rightIcon(); as rightIcon) {
                <div class="flex-none">
                    <aui-icon [icon]="rightIcon"></aui-icon>
                </div>
            }
        </div>
    `,
})
export class AuiBadgeComponent {
    color = input<AuiColor>('gray');
    label = input<string | null | undefined>(null);
    buttonStyle = input<AuiButtonStyleType>('solid');
    icon = input<AuiIcon | null>(null);
    rightIcon = input<AuiIcon | null>(null);

    isBadgeEmpty = computed(() => !this.label() && !this.icon() && !this.rightIcon());

    rounded = input<boolean>(false);

    classes = hostBinding(
        'attr.class',
        computed(() => {
            const color = this.color();
            const buttonStyle = this.buttonStyle();
            const isBadgeEmpty = this.isBadgeEmpty();
            const tailwindClasses = [
                'aui-font-regular-12',
                'border-solid',
                'border',
                'text-center',
                'transition-all',
                'focus:outline-none focus:ring',
                'rounded-full',
                'inline-block',
                'max-w-max',
                'max-h-max',
            ];

            if (isBadgeEmpty) {
                tailwindClasses.push('hidden');
            }

            const borders: Record<AuiColor, string[]> = {
                transparent: ['border-transparent', 'hover:border-transparent', 'active:border-transparent'],
                gray: ['border-gray', 'hover:border-gray-dark', 'active:border-gray-darken'],
                primary: ['border-primary', 'hover:border-primary-dark', 'active:border-primary-darken'],
                success: ['border-success', 'hover:border-success-dark', 'active:border-success-darken'],
                danger: ['border-danger', 'hover:border-danger-dark', 'active:border-danger-darken'],
                warning: ['border-warning', 'hover:border-warning-dark', 'active:border-warning-darken'],
                info: ['border-info', 'hover:border-info-dark', 'active:border-info-darken'],
                help: ['border-help', 'hover:border-help-dark', 'active:border-help-darken'],
            };

            const solidBackgrounds: Record<AuiColor, string[]> = {
                transparent: ['bg-transparent', 'hover:bg-transparent', 'active:bg-transparent'],
                gray: ['bg-gray', 'hover:bg-gray-dark', 'active:bg-gray-darken'],
                primary: ['bg-primary', 'hover:bg-primary-dark', 'active:bg-primary-darken'],
                success: ['bg-success', 'hover:bg-success-dark', 'active:bg-success-darken'],
                danger: ['bg-danger', 'hover:bg-danger-dark', 'active:bg-danger-darken'],
                warning: ['bg-warning', 'hover:bg-warning-dark', 'active:bg-warning-darken'],
                info: ['bg-info', 'hover:bg-info-dark', 'active:bg-info-darken'],
                help: ['bg-help', 'hover:bg-help-dark', 'active:bg-help-darken'],
            };

            const decentBackgrounds: Record<AuiColor, string[]> = {
                transparent: [],
                gray: ['bg-gray/[.1]', 'hover:bg-gray-dark/[.2]', 'active:bg-gray-darken/[.3]'],
                primary: ['bg-primary/[.1]', 'hover:bg-primary-dark/[.2]', 'active:bg-primary-darken/[.3]'],
                success: ['bg-success/[.1]', 'hover:bg-success-dark/[.2]', 'active:bg-success-darken/[.3]'],
                danger: ['bg-danger/[.1]', 'hover:bg-danger-dark/[.2]', 'active:bg-danger-darken/[.3]'],
                warning: ['bg-warning/[.1]', 'hover:bg-warning-dark/[.2]', 'active:bg-warning-darken/[.3]'],
                info: ['bg-info/[.1]', 'hover:bg-info-dark/[.2]', 'active:bg-info-darken/[.3]'],
                help: ['bg-help/[.1]', 'hover:bg-help-dark/[.2]', 'active:bg-help-darken/[.3]'],
            };

            const transparentBackgrounds: Record<AuiColor, string[]> = {
                transparent: [],
                gray: ['bg-gray/[0]', 'hover:bg-gray-dark/[.1]', 'active:bg-gray-darken/[.2]'],
                primary: ['bg-primary/[0]', 'hover:bg-primary-dark/[.1]', 'active:bg-primary-darken/[.2]'],
                success: ['bg-success/[0]', 'hover:bg-success-dark/[.1]', 'active:bg-success-darken/[.2]'],
                danger: ['bg-danger/[0]', 'hover:bg-danger-dark/[.1]', 'active:bg-danger-darken/[.2]'],
                warning: ['bg-warning/[0]', 'hover:bg-warning-dark/[.1]', 'active:bg-warning-darken/[.2]'],
                info: ['bg-info/[0]', 'hover:bg-info-dark/[.1]', 'active:bg-info-darken/[.2]'],
                help: ['bg-help/[0]', 'hover:bg-help-dark/[.1]', 'active:bg-help-darken/[.2]'],
            };

            const reverseText: Record<AuiColor, string[]> = {
                transparent: [],
                gray: ['text-gray-text'],
                primary: ['text-primary-text'],
                success: ['text-success-text'],
                danger: ['text-danger-text'],
                warning: ['text-warning-text'],
                info: ['text-info-text'],
                help: ['text-help-text'],
            };

            const coloredText: Record<AuiColor, string[]> = {
                transparent: ['text-inherit', 'hover:text-inherit', 'active:text-inherit'],
                gray: ['text-gray', 'hover:text-gray', 'active:text-gray'],
                primary: ['text-primary', 'hover:text-primary-dark', 'active:text-primary-darken'],
                success: ['text-success', 'hover:text-success-dark', 'active:text-success-darken'],
                danger: ['text-danger', 'hover:text-danger-dark', 'active:text-danger-darken'],
                warning: ['text-warning', 'hover:text-warning-dark', 'active:text-warning-darken'],
                info: ['text-info', 'hover:text-info-dark', 'active:text-info-darken'],
                help: ['text-help', 'hover:text-help-dark', 'active:text-help-darken'],
            };

            const focusRing: Record<AuiColor, string[]> = {
                transparent: [],
                gray: ['ring-gray'],
                primary: ['ring-primary'],
                success: ['ring-success'],
                danger: ['ring-danger'],
                warning: ['ring-warning'],
                info: ['ring-info'],
                help: ['ring-help'],
            };

            //       </div>
            // @if (this.icon(); as icon) {
            //   <div class="p-1 text-center">
            //     <aui-icon [icon]="icon"></aui-icon>
            //   </div>
            // } @else {
            //   <div class="px-2 text-center py-1 max-w-max">
            //     <span>
            //       {{ this.label() }}
            //     </span>
            //   </div>

            const label = this.label();
            if (!label) {
                tailwindClasses.push('px-1 text-center');
            } else {
                tailwindClasses.push('px-2');
            }

            if (buttonStyle === 'solid') {
                tailwindClasses.push(...borders[color]);
                tailwindClasses.push(...solidBackgrounds[color]);
                tailwindClasses.push(...reverseText[color]);
            }
            if (buttonStyle === 'outline') {
                tailwindClasses.push(...borders[color]);
                tailwindClasses.push(...solidBackgrounds['transparent']);
                tailwindClasses.push(...coloredText[color]);
            }
            if (buttonStyle === 'decent') {
                tailwindClasses.push(...borders['transparent']);
                tailwindClasses.push(...decentBackgrounds[color]);
                tailwindClasses.push(...coloredText[color]);
            }
            if (buttonStyle === 'text') {
                tailwindClasses.push(...borders['transparent']);
                tailwindClasses.push(...transparentBackgrounds[color]);
                tailwindClasses.push(...coloredText[color]);
            }

            // ring
            tailwindClasses.push(...focusRing[color]);

            return tailwindClasses.join(' ');
        })
    );
}
